import CustomTable from "components/CustomTable";
import FilterDate from "components/CustomFilterMenu/FilterDate";
import { LoyaltyListToolbar, LoyaltyTableActions } from "features/LoyaltyPackage/presentation/components";
import { useLoyaltyPackage, usePeriodCreatedByNameMap } from "features/LoyaltyPackage/data/hooks";
import {
  formatLoyaltyDateRangeMultiline,
  getLoyaltyApplyScopeLabel,
  getLoyaltyCreatedByDisplay,
  getLoyaltyTimeIntervalLabel,
} from "features/LoyaltyPackage/core/utils";
import { t } from "i18next";
import { Link, useHistory } from "react-router-dom";
import { notify } from "utils/helperFuncs";
import DateRangeColumnFilter from "./components/DateRangeColumnFilter";
import ProgramColumnFilter from "./components/ProgramColumnFilter";
import usePackageList from "./usePackageList";

function PackageList() {
  const history = useHistory();
  const { TEXT_STATUS_LOYALTY } = useLoyaltyPackage();

  const {
    searchTerm,
    handleSearch,
    periods,
    paginationData,
    loading,
    switchLoading,
    handleTableChange,
    handleSwitchStatus,
    isEditable,
    pagination,
    programOptions,
    programsLoading,
    programsLoadingMore,
    hasMorePrograms,
    loyaltyPackageIds,
    columnTimeRange,
    handleToolbarDateFilter,
    handleColumnDateFilter,
    handleProgramFilter,
    handleProgramFilterOpenChange,
    handleLoadMorePrograms,
  } = usePackageList();

  const createdByNameById = usePeriodCreatedByNameMap(periods);

  const handleToggleConfirm = async (record) => {
    try {
      await handleSwitchStatus(record, !record.isActive);
      notify.success({ message: t("customer.updateStatusSuccess") });
    } catch (err) {
      notify.error({
        message: err?.message || t("loyaltyPackage.package.updateFailed"),
      });
    }
  };

  const columns = [
    {
      title: t("common.index"),
      key: "index",
      width: 60,
      align: "center",
      render: (_, __, index) => pagination.offset + index + 1,
    },
    {
      title: t("loyaltyPackage.package.name"),
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <Link to={`/loyalty-package/package/${record.id}`} className="loyalty-package-link">
          {title}
        </Link>
      ),
    },
    {
      title: t("loyaltyPackage.package.timeIntervalType"),
      dataIndex: "timeInterval",
      key: "timeInterval",
      width: 140,
      render: (timeInterval) => getLoyaltyTimeIntervalLabel(timeInterval),
    },
    {
      title: t("loyaltyPackage.package.timePeriod"),
      key: "timePeriod",
      width: 200,
      filterDropdown: <DateRangeColumnFilter onFilter={handleColumnDateFilter} defaultValue={columnTimeRange} />,
      render: (_, record) => {
        const range = formatLoyaltyDateRangeMultiline(record.startTime, record.endTime);
        if (typeof range === "string") return range;
        return (
          <span className="loyalty-date-range">
            {range.start} - <br /> {range.end}
          </span>
        );
      },
    },
    {
      title: t("loyaltyPackage.package.belongsToProgram"),
      key: "loyaltyPackage",
      width: 240,
      filterDropdown: (
        <ProgramColumnFilter
          options={programOptions}
          loading={programsLoading}
          loadingMore={programsLoadingMore}
          hasMore={hasMorePrograms}
          onLoadMore={handleLoadMorePrograms}
          defaultValue={loyaltyPackageIds}
          onFilter={handleProgramFilter}
        />
      ),
      onFilterDropdownVisibleChange: handleProgramFilterOpenChange,
      render: (_, record) => (
        <Link to={`/loyalty-package/program/${record.loyaltyPackageId}`} className="loyalty-package-link">
          {record.loyaltyPackage?.title ?? "—"}
        </Link>
      ),
    },
    {
      title: t("loyaltyPackage.package.applyScope"),
      key: "applyScope",
      width: 140,
      render: (_, record) => getLoyaltyApplyScopeLabel(record.applyScope, record.numOfProducts),
    },
    {
      title: t("loyaltyPackage.package.status"),
      key: "status",
      align: "center",
      width: 140,
      render: (_, record) => TEXT_STATUS_LOYALTY[record.status] ?? record.status,
    },
    {
      title: t("loyaltyPackage.package.createdBy"),
      key: "createdBy",
      width: 180,
      render: (_, record) => getLoyaltyCreatedByDisplay(record, createdByNameById),
    },
    {
      title: t("loyaltyPackage.package.actions"),
      key: "actions",
      align: "center",
      fixed: "right",
      width: 180,
      className: "loyalty-action-column",
      render: (_, record) => (
        <LoyaltyTableActions
          isActive={record.isActive}
          switchLoading={switchLoading}
          onToggle={() => handleToggleConfirm(record)}
          confirmOnMessage={t("loyaltyPackage.package.confirmOn")}
          confirmOffMessage={t("loyaltyPackage.package.confirmOff")}
          showClone
          onClone={() => history.push(`/loyalty-package/package/create?clone=${record.id}`)}
          cloneTooltip={t("common.clone")}
          showEdit={isEditable(record)}
          onEdit={() => history.push(`/loyalty-package/package/edit/${record.id}`)}
          editTooltip={t("common.edit")}
        />
      ),
    },
  ];

  return (
    <div className="package-list-container">
      <LoyaltyListToolbar
        filters={<FilterDate onChangeDate={handleToolbarDateFilter} title={t("loyaltyPackage.filterPromotionTime")} />}
        searchValue={searchTerm}
        onSearchChange={handleSearch}
        searchPlaceholder={t("loyaltyPackage.searchPackage")}
        createLabel={t("common.addPackage")}
        onCreate={() => history.push("/loyalty-package/package/create")}
      />

      <div className="custom-table-list-loyalty">
        <CustomTable
          columns={columns}
          dataSource={periods}
          rowKey="id"
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          pagination={{
            total: paginationData.total,
            pageSize: pagination.limit,
            current: Math.floor(pagination.offset / pagination.limit) + 1,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
}

export default PackageList;
